import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Mail, Phone, Search, User, AlertCircle, Home } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/lib/axiosInstance'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar"
import Helper from '@/lib/helper'
import type { ListingEnquiries } from '@/types/PropertyListingEnquiries'

const Leads = () => {
    const [offset, setOffset] = useState(0);
    const [length] = useState(50);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [activeFilters, setActiveFilters] = useState<{
        unread?: number;
        registered?: boolean;
        nonRegistered?: boolean;
    }>({});
    
    const [leads, setLeads] = useState<ListingEnquiries[]>([]);
    const [selectedLead, setSelectedLead] = useState<ListingEnquiries | null>(null);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [showHistory, setShowHistory] = useState<boolean>(false);
    
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastItemRef = useRef<HTMLDivElement | null>(null);

    const filters = [
        { id: 'unread', label: 'Unread' },
        { id: 'registered', label: 'Registered' },
        { id: 'nonRegistered', label: 'Non-registered' },
    ];

    const formatPhone = (contact: string) => {
        let phone = contact.replace(/\D/g, '').replace(/^0+/, '');
        if (!phone) {
            return phone;
        }

        if (!phone.startsWith('60')) {
            phone = `60${phone}`;
        }
        return phone
    }

    const getLeads = async (isLoadMore = false) => {
        if (loading || (!isLoadMore && offset > 0)) return;
        
        try {
            setLoading(true);
            
            let filterParams = '';
            if (activeFilters.unread !== undefined) {
                filterParams += `&unread=${activeFilters.unread}`;
            }
            if (activeFilters.registered) {
                filterParams += `&consumerId=notNull`;
            }
            if (activeFilters.nonRegistered) {
                filterParams += `&consumerId=null`;
            }

            const currentOffset = isLoadMore ? offset : 0;
            const response = await api.get(
                `/me/listings/enquiries?offset=${currentOffset}&length=${length}${search ? `&search=${search}` : ''}${filterParams}`
            );
            
            if (!response.data.status) {
                throw new Error(response.data.error)
            }
            
            const newLeads = response.data.enquiries;
            
            if (isLoadMore) {
                setLeads(prev => [...prev, ...newLeads]);
            } else {
                setLeads(newLeads);
            }
            
            setTotal(response.data.total);
            setHasMore(newLeads.length === length);
        } catch (err: any) {
            toast.error(err.response?.data.error ?? err.message ?? 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const loadMore = useCallback(() => {
        if (hasMore && !loading) {
            setOffset(prev => prev + length);
        }
    }, [hasMore, loading, length]);

    const handleSearch = () => {
        setSearch(searchInput);
    };

    const toggleFilter = (filterId: string) => {
        setActiveFilters(prev => {
            const newFilters = { ...prev };
            
            if (filterId === 'unread') {
                if (newFilters.unread === 1) {
                    delete newFilters.unread;
                } else {
                    newFilters.unread = 1;
                }
            } else if (filterId === 'registered') {
                if (newFilters.registered) {
                    delete newFilters.registered;
                } else {
                    newFilters.registered = true;
                    delete newFilters.nonRegistered;
                }
            } else if (filterId === 'nonRegistered') {
                if (newFilters.nonRegistered) {
                    delete newFilters.nonRegistered;
                } else {
                    newFilters.nonRegistered = true;
                    delete newFilters.registered;
                }
            }
            
            return newFilters;
        });
    };

    const clearFilters = () => {
        setActiveFilters({});
        setSearch('');
        setSearchInput('');
    };

    const isLeadOld = (createdAt: string) => {
        const daysDiff = Math.floor((new Date().getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
        return daysDiff > 60;
    };

    const isFilterActive = (filterId: string) => {
        if (filterId === 'unread') {
            return activeFilters.unread === 1;
        } else if (filterId === 'registered') {
            return activeFilters.registered === true;
        } else if (filterId === 'nonRegistered') {
            return activeFilters.nonRegistered === true;
        }
        return false;
    };

    const readEnquiry = async (leadId: number) => {
        try {
            const lead = leads.find(x => x.id === leadId);
            if(!lead) return

            // if(lead.readAt) return
    
            const response = await api.get(`/me/listings/${lead.listingId}/enquiries/${leadId}/read`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            const updatedReadAt = response.data.enquiry.readAt;
            setLeads((prevLeads) =>
                prevLeads.map((item) =>
                    item.id === leadId
                        ? { ...item, readAt: updatedReadAt }
                        : item
                )
            );

            setSelectedLead((prev) =>
                prev && prev.id === leadId
                    ? { ...prev, readAt: updatedReadAt }
                    : prev
            );
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    useEffect(() => {
        if (offset > 0) {
            getLeads(true);
        }
    }, [offset]);

    useEffect(() => {
        setOffset(0);
        setLeads([]);
        setHasMore(true);
        getLeads(false);
    }, [search, activeFilters]);

    useEffect(() => {
        const options = {
            root: scrollContainerRef.current,
            rootMargin: '100px',
            threshold: 0.1
        };

        observerRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                loadMore();
            }
        }, options);

        if (lastItemRef.current) {
            observerRef.current.observe(lastItemRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, loading, loadMore]);

    return (
        <div className="h-screen flex flex-col">
            <div className='mb-4 flex justify-between items-center'>
                <h5 className='h5 uppercase bold'>Lead Management</h5>
            </div>
            <div className="p-4 border-b bg-white flex-shrink-0">
                <div className="flex gap-3 items-center mb-3">
                    <div className="flex-1 relative">
                        <Search 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                        size={18}/>
                        <Input
                        placeholder="Search enquirer name, email or listing"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="pl-10"/>
                    </div>
                    <Button onClick={handleSearch} variant="outline">
                        <Search size={16} />
                    </Button>
                </div>

                <div className="flex gap-2 items-center flex-wrap">
                    {
                        filters.map(filter => (
                            <Button
                            key={filter.id}
                            variant={isFilterActive(filter.id) ? "default" : "outline"}
                            size="sm"
                            onClick={() => toggleFilter(filter.id)}>
                                {filter.label}
                            </Button>
                        ))
                    }
                    {
                        (Object.keys(activeFilters).length > 0 || search) && (
                            <Button 
                            variant="ghost"
                            size="sm" 
                            onClick={clearFilters}>
                                Clear
                            </Button>
                        )
                    }
                </div>
            </div>

            <div className="flex-1 flex min-h-0 relative overflow-hidden">
                {/* Leads List */}
                <div className={`${
                    selectedLead ? '-translate-x-full lg:translate-x-0 opacity-0 lg:opacity-100' : 'translate-x-0 opacity-100'
                } w-full lg:w-[480px] border-r bg-white flex flex-col h-full transition-all duration-500 ease-in-out absolute lg:relative z-10`}>
                    <div className="p-4 border-b flex-shrink-0">
                        <h3 className="font-semibold text-lg">Leads</h3>
                        <p className="text-sm text-gray-500">{total} total</p>
                    </div>
                    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
                        {
                            leads && leads.length > 0 ? (
                                <div>
                                    {
                                        leads.map((lead: any, index) => (
                                            <div
                                            key={lead.id}
                                            ref={index === leads.length - 1 ? lastItemRef : null}
                                            onClick={() => {
                                                setSelectedLead(lead);
                                                readEnquiry(lead.id)
                                            }}
                                            className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                                                selectedLead?.id === lead.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                                            }`}>
                                            <div className="flex items-start gap-3">
                                                {
                                                    !lead.readAt && (
                                                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                                    )
                                                }
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <h4 className="font-semibold text-sm truncate">{lead.email}</h4>
                                                        {
                                                            lead.consumerId && (
                                                                <Badge variant="outline" className="text-xs">Consumer</Badge>
                                                            )
                                                        }
                                                    </div>
                                                    
                                                    <div className="space-y-1">
                                                        {
                                                            lead.interactions && lead.interactions.length > 0 && (() => {
                                                                const latestInteraction = lead.interactions.reduce((latest, current) =>
                                                                    new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest
                                                                );

                                                                return (
                                                                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                                                        <span className="font-medium">Last interaction:</span>
                                                                        <span>
                                                                            {Helper.formatDateOnly(latestInteraction.createdAt)}
                                                                        </span>
                                                                        <span className="text-gray-400">at</span>
                                                                        <span>
                                                                            {Helper.formatTimeOnly(latestInteraction.createdAt)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()
                                                        }

                                                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                            <span className="font-medium">Enquired:</span>
                                                            <span>
                                                                {Helper.formatDateOnly(lead.createdAt)}
                                                            </span>
                                                            <span className="text-gray-400">at</span>
                                                            <span>
                                                                {Helper.formatTimeOnly(lead.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        ))
                                    }
                                    {
                                        loading && (
                                            <div className="flex justify-center items-center py-4">
                                                <Spinner />
                                            </div>
                                        )
                                    }
                                </div>
                            ) : loading ? (
                                <div className="flex justify-center items-center py-8">
                                    <Spinner />
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                                    <User size={48} className="mb-2 opacity-20" />
                                    <p>No leads found</p>
                                </div>
                            )
                        }
                    </div>
                </div>

                <div className={`${
                    selectedLead ? 'translate-x-0 opacity-100' : 'translate-x-full lg:translate-x-0 opacity-0 lg:opacity-100'
                } flex flex-1 bg-gray-50 min-w-0 h-full absolute lg:relative inset-0 lg:inset-auto z-20 lg:z-auto transition-all duration-500 ease-in-out`}>
                    <div className="flex-1 overflow-y-auto w-full">
                        {
                            selectedLead ? (
                                <div className="p-4 lg:p-6 w-full">
                                    <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mb-4 lg:hidden"
                                    onClick={() => setSelectedLead(null)}>
                                        ← Back to leads
                                    </Button>
                                    {/* {
                                        selectedLead.lastReadAt && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                <span className="font-medium">Last viewed:</span>
                                                <span>
                                                    {
                                                        new Date(selectedLead.createdAt).toLocaleDateString('en-GB', {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric',
                                                        })
                                                    }
                                                </span>
                                                <span className="text-gray-400">at</span>
                                                <span>
                                                    {
                                                        new Date(selectedLead.createdAt).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true,
                                                        })
                                                    }
                                                </span>
                                            </div>
                                        )
                                    } */}
                                    <div>

                                    </div>
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="flex items-center gap-3">
                                            <Avatar className='h-16 w-16'>
                                                <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${selectedLead.avatar}`}/>
                                                <AvatarFallback>
                                                    {selectedLead.firstName[0]} {selectedLead.lastName[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <h2 className="text-2xl font-semibold">{selectedLead.firstName} {selectedLead.lastName}</h2>
                                            </div>
                                        </div>
                                        <div>
                                            <Button
                                            size="sm"
                                            className="mr-1"
                                            onClick={() => window.open(`tel:+${formatPhone(selectedLead.contact)}`)}>
                                                <Phone size={14} />
                                            </Button>
                                            <Button
                                            size="sm"
                                            onClick={() => window.open(`mailto:${selectedLead.email}`)}>
                                                <Mail size={14} />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="mb-6 space-y-2">
                                        <div className="flex items-center gap-2 text-gray-700 text-sm">
                                            <Mail size={14} />
                                            <span>{selectedLead.email}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-700 text-sm">
                                            <Phone size={14} />
                                            <span>{selectedLead.contact || 'N/A'}</span>
                                        </div>
                                    </div>
                                    {
                                        isLeadOld(selectedLead.createdAt) && (
                                            <Card className="mb-6 border-orange-200 bg-orange-50">
                                                <CardContent className="p-4">
                                                    <div className="flex gap-3">
                                                        <Badge variant="secondary">OLD</Badge>
                                                        <div>
                                                            <p className="text-sm text-gray-700">
                                                                This lead is more than 60 days old and will no longer be updated. 
                                                                To continue contacting this lead, please obtain enquirer's consent.{' '}
                                                                <a href="#" className="text-blue-600 hover:underline">Learn more</a>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    }
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold mb-4">Interested In</h3>
                                        {
                                            selectedLead.listing && (
                                                <div
                                                key={selectedLead.listing.id}
                                                onClick={() => {
                                                    window.open(`${import.meta.env.VITE_WWW_URL}${Helper.buildListingUrl(selectedLead.listing)}`, '_blank');
                                                }}
                                                className={`bg-white overflow-hidden rounded-lg shadow-md cursor-pointer transition-all hover:shadow-xl`}>
                                                {
                                                    selectedLead.listing.medias && selectedLead.listing.medias.length > 0 && (
                                                        <div className="relative h-32 lg:h-64 bg-gray-100">
                                                            <img
                                                                src={`${import.meta.env.VITE_AWS_S3}/${selectedLead.listing.medias[0].path}`}
                                                                alt={selectedLead.listing.headline}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => (e.currentTarget.style.display = 'none')}
                                                            />
                                                            <Badge variant={'default'} className='absolute top-1 left-1'>
                                                                {selectedLead.listing.forSaleOrRentId === 1 ? 'SALE' : 'RENT'}
                                                            </Badge>
                                                        </div>
                                                    )
                                                }
                                                <div className="p-2.5 lg:p-3">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        RM {Helper.formatWithoutDecimal(Number(selectedLead.listing.price))}
                                                    </div>
                                                    <div className="text-xs lg:text-sm font-medium text-gray-700 line-clamp-2">
                                                        {selectedLead.listing.headline}
                                                    </div>
                                                    <div className="text-xs text-gray-500 line-clamp-1">
                                                        {selectedLead.listing.rawAddress}
                                                    </div>
                                                    <span className='text-xs'>View details</span>
                                                    {
                                                        (selectedLead.listing.noOfBedrooms || 
                                                        selectedLead.listing.noOfBathrooms || 
                                                        selectedLead.listing.noOfParkingSpots) && (
                                                            <div className="flex items-center gap-2.5 text-xs text-gray-600">
                                                                {
                                                                    selectedLead.listing.noOfBedrooms && (
                                                                        <div className="flex items-center gap-1">
                                                                            <Home size={13} />
                                                                            <span>{selectedLead.listing.noOfBedrooms}</span>
                                                                        </div>
                                                                    )
                                                                }
                                                                {
                                                                    selectedLead.listing.noOfBathrooms && (
                                                                        <div className="flex items-center gap-1">
                                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                            </svg>
                                                                            <span>{selectedLead.listing.noOfBathrooms}</span>
                                                                        </div>
                                                                    )
                                                                }
                                                                {
                                                                    selectedLead.listing.noOfParkingSpots && (
                                                                        <div className="flex items-center gap-1">
                                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 013.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                                                            </svg>
                                                                            <span>{selectedLead.listing.noOfParkingSpots}</span>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                            )
                                        }
                                    </div>
                                    <div className="mb-6">
                                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Last interest shown</h4>
                                        {
                                            selectedLead.interactions && selectedLead.interactions.length > 0 ? (
                                                <>
                                                    <button 
                                                    onClick={() => setShowHistory(!showHistory)}
                                                    className="text-blue-600 text-sm hover:underline cursor-pointer">
                                                        {showHistory ? 'Hide history' : `View history`}
                                                    </button>
                                                    <div 
                                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                                        showHistory ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'
                                                    }`}>
                                                        <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                                                            {
                                                                selectedLead.interactions.map((interaction, index) => (
                                                                    <div key={interaction.id} className="text-sm">
                                                                        <p className="mb-1">
                                                                            {Helper.formatDateTime(interaction.createdAt)} • <span className="capitalize">{interaction.type}</span>
                                                                            {index === 0 && <span className="ml-2 text-xs text-gray-500">(Most recent)</span>}
                                                                        </p>
                                                                        {
                                                                            interaction.remarks && (
                                                                                <p className="text-gray-600 text-xs mt-1">{interaction.remarks}</p>
                                                                            )
                                                                        }
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <p className="text-sm text-gray-500">No interactions yet</p>
                                            )
                                        }
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                                            Lead Insights
                                            <AlertCircle size={16} className="text-gray-400" />
                                        </h3>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400">
                                    <div className="text-center">
                                        <User size={36} className="mx-auto mb-4 opacity-20" />
                                        <p className="text-lg">Select a lead to view details</p>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Leads;
